import { type ReactNode, useId } from 'react';
import { useTranslation } from '@/shared/i18n/use-translation';
import { NativeSelect } from '@/shared/ui/native-select/native-select';
import { NativeSelectOption } from '@/shared/ui/native-select/native-select-option';
import { FieldShell } from './field-shell';

type SelectFieldProps<V extends string> = {
  readonly label: string;
  readonly value: V;
  readonly onChange: (value: V) => void;
  /** Labels are English keys, translated here. */
  readonly options: readonly { readonly value: V; readonly label: string }[];
  readonly error?: string | undefined;
  readonly hint?: ReactNode;
};

/** A native select: the best picker on phones. */
export function SelectField<V extends string>({ value, onChange, options, ...shell }: SelectFieldProps<V>) {
  const id = useId();
  const { t } = useTranslation();
  return (
    <FieldShell id={id} {...shell}>
      <NativeSelect
        id={id}
        value={value}
        // The options are exactly the values of V.
        onChange={(event) => onChange(event.target.value as V)}
        className="w-full [&_select]:h-10 [&_select]:text-base md:[&_select]:h-9 md:[&_select]:text-sm"
      >
        {options.map((option) => (
          <NativeSelectOption key={option.value} value={option.value}>
            {t(option.label)}
          </NativeSelectOption>
        ))}
      </NativeSelect>
    </FieldShell>
  );
}
